// The poster wall (N): what is billed at the clubs and still ahead.
//
// A night nobody can find advertised is not a night — this is the surface
// that turns a booking into an institution. Public and unauthenticated on
// purpose: you should be able to see what's on before you care who you are.

import { areaLabel, type NightBill } from "@bebop/shared";
import { fetchNights } from "../core/nights";
import { flyerCard } from "./bench";
import { escapeHtml } from "./escape";

/** "tonight, 21:40" / "Thu 22:00" / "soon" — never a raw timestamp. */
function when(iso: string | null): string {
  if (!iso) return "soon";
  const at = new Date(iso);
  const now = new Date();
  const time = at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (at.toDateString() === now.toDateString()) return `tonight, ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (at.toDateString() === tomorrow.toDateString()) return `tomorrow, ${time}`;
  return `${at.toLocaleDateString(undefined, { weekday: "short" })} ${time}`;
}

export class NightsPanel {
  private root: HTMLDivElement;
  private body: HTMLDivElement;
  private open = false;

  constructor(parent: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "panel-float nights-panel";
    this.root.innerHTML = `
      <div class="panel-title">WHAT'S ON <span class="panel-hint">N to close</span></div>
      <div class="nights-body"></div>
    `;
    this.root.style.display = "none";
    parent.append(this.root);
    this.body = this.root.querySelector(".nights-body") as HTMLDivElement;
  }

  toggle(): void {
    this.open = !this.open;
    this.root.style.display = this.open ? "" : "none";
    if (this.open) void this.refresh();
  }

  /** Repaint only if somebody is looking at it. */
  async refreshIfOpen(): Promise<void> {
    if (this.open) await this.refresh();
  }

  private async refresh(): Promise<void> {
    this.body.innerHTML = `<div class="panel-empty">reading the wall…</div>`;
    try {
      this.render(await fetchNights());
    } catch {
      this.body.innerHTML = `<div class="panel-empty">the wall is bare (try again)</div>`;
    }
  }

  private render(nights: NightBill[]): void {
    if (nights.length === 0) {
      this.body.innerHTML = `<div class="panel-empty">Nothing billed. Claim a room (/claim) and book somebody.</div>`;
      return;
    }
    this.body.innerHTML = "";
    for (const night of nights) {
      const card = document.createElement("div");
      card.className = "night-card";
      // A night somebody printed a flyer for wears it. This is the moment
      // a crafter's name goes up on the wall beside the artist's.
      const flyer = night.flyer
        ? // flyerCard escapes its own arguments — pass raw text in.
          flyerCard(
            night.flyer.look,
            night.flyer.title,
            `${night.artistName} · ${when(night.scheduledFor)}`,
            `printed by ${night.flyer.makerName}`,
          )
        : "";
      card.innerHTML = `
        ${flyer}
        <div class="night-title">${escapeHtml(night.title) || "an unnamed night"}</div>
        <div class="night-line"><span class="night-artist">${escapeHtml(night.artistName)}</span> · ${escapeHtml(areaLabel(night.clubId))}</div>
        <div class="night-when">${when(night.scheduledFor)} · presented by ${escapeHtml(night.hostName)}</div>
      `;
      this.body.append(card);
    }
  }
}
